import React, { useState, useCallback, useRef, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import citiesData from "../../../assets/images/us-cities.json";
import { getPropertySuggests } from "../../../API/search";
import { abbrState } from "../../../utils/stateToAbbr";
import "./SearchBar.css";
import { useMediaQuery } from "@material-ui/core";

// State array with both full name and abbreviation
const usStates = [
  { name: "Alabama", abbr: "AL" },
  { name: "Alaska", abbr: "AK" },
  { name: "Arizona", abbr: "AZ" },
  { name: "Arkansas", abbr: "AR" },
  { name: "California", abbr: "CA" },
  { name: "Colorado", abbr: "CO" },
  { name: "Connecticut", abbr: "CT" },
  { name: "Delaware", abbr: "DE" },
  { name: "Florida", abbr: "FL" },
  { name: "Georgia", abbr: "GA" },
  { name: "Hawaii", abbr: "HI" },
  { name: "Idaho", abbr: "ID" },
  { name: "Illinois", abbr: "IL" },
  { name: "Indiana", abbr: "IN" },
  { name: "Iowa", abbr: "IA" },
  { name: "Kansas", abbr: "KS" },
  { name: "Kentucky", abbr: "KY" },
  { name: "Louisiana", abbr: "LA" },
  { name: "Maine", abbr: "ME" },
  { name: "Maryland", abbr: "MD" },
  { name: "Massachusetts", abbr: "MA" },
  { name: "Michigan", abbr: "MI" },
  { name: "Minnesota", abbr: "MN" },
  { name: "Mississippi", abbr: "MS" },
  { name: "Missouri", abbr: "MO" },
  { name: "Montana", abbr: "MT" },
  { name: "Nebraska", abbr: "NE" },
  { name: "Nevada", abbr: "NV" },
  { name: "New Hampshire", abbr: "NH" },
  { name: "New Jersey", abbr: "NJ" },
  { name: "New Mexico", abbr: "NM" },
  { name: "New York", abbr: "NY" },
  { name: "North Carolina", abbr: "NC" },
  { name: "North Dakota", abbr: "ND" },
  { name: "Ohio", abbr: "OH" },
  { name: "Oklahoma", abbr: "OK" },
  { name: "Oregon", abbr: "OR" },
  { name: "Pennsylvania", abbr: "PA" },
  { name: "Rhode Island", abbr: "RI" },
  { name: "South Carolina", abbr: "SC" },
  { name: "South Dakota", abbr: "SD" },
  { name: "Tennessee", abbr: "TN" },
  { name: "Texas", abbr: "TX" },
  { name: "Utah", abbr: "UT" },
  { name: "Vermont", abbr: "VT" },
  { name: "Virginia", abbr: "VA" },
  { name: "Washington", abbr: "WA" },
  { name: "West Virginia", abbr: "WV" },
  { name: "Wisconsin", abbr: "WI" },
  { name: "Wyoming", abbr: "WY" },
  { name: "Puerto Rico", abbr: "PR" },
];

const SearchBar = ({
  placeholder = "Enter Address, City, State, Zipcode, County or APN",
  className = "",
  variant = "outlined",
  size = "small",
  fullWidth = false,
  from = "",
}) => {
  const history = useHistory();
  const location = useLocation();
  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const isHome = location.pathname === "/";

  // Initialize input value from URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchbarValue = urlParams.get("searchbar");
    if (searchbarValue && !inputValue) {
      setInputValue(searchbarValue);
    }
  }, []); // Only run on mount

  const fetchCitySuggestions = useCallback((input) => {
    if (!input) {
      return [];
    }

    const lowerInput = input.toLowerCase();

    // Match both state names and abbreviations
    const matchingStates = usStates
      .filter(
        (state) =>
          state.name.toLowerCase().startsWith(lowerInput) ||
          state.abbr.toLowerCase() === lowerInput,
      )
      .map((state) => ({
        address: state.name,
        source: "state",
        abbr: state.abbr,
      }))
      .slice(0, 3);

    const filteredCities = citiesData
      .filter((item) => {
        const cityMatch = item.city.toLowerCase().startsWith(lowerInput);
        const stateMatch = item.state_id.toLowerCase().startsWith(lowerInput);
        const fullMatch = `${item.city}, ${item.state_id}`
          .toLowerCase()
          .startsWith(lowerInput);
        return cityMatch || stateMatch || fullMatch;
      })
      .map((item) => ({
        address: `${item.city}, ${item.state_id}`,
        source: "city",
      }))
      .slice(0, 5);

    return [...matchingStates, ...filteredCities];
  }, []);

  // Helper function to get URL params based on current location
  const getUrlParams = () => {
    // Check if we're on the search page
    const isSearchPage = location.pathname.includes("/search");

    if (isSearchPage) {
      // On search page, use the current URL params
      return new URLSearchParams(location.search);
    } else {
      // Not on search page, use default location params if available
      return global.defaultLocation
        ? new URLSearchParams(global.defaultLocation)
        : new URLSearchParams();
    }
  };

  const handleInputChange = useCallback(
    async (event, newInputValue, reason) => {
      if (reason === "input") {
        setInputValue(newInputValue);
        if (newInputValue) {
          try {
            const jsonSuggestions = fetchCitySuggestions(newInputValue);
            const searchData = await getPropertySuggests({
              search: newInputValue,
            });
            if (searchData && searchData.length > 0) {
              const addresses = searchData
                .filter((item) => item.type === "fulladress")
                .map((item) => ({
                  address: item.name,
                  id: item.id,
                  type: item.type,
                  source: "api",
                  loc_address: item.loc_address,
                  loc_city: item.loc_city,
                  state: item.state,
                  zipcode: item.zipcode,
                  county: item.county,
                }))
                .slice(0, 5);

              const sortedSuggestions = [...jsonSuggestions, ...addresses];
              setSuggestions(sortedSuggestions);

              // Ensure dropdown opens when there are suggestions
              if (sortedSuggestions.length > 0) {
                setIsOpen(true);
              }
            } else {
              setSuggestions(jsonSuggestions);

              // Ensure dropdown opens when there are suggestions
              if (jsonSuggestions.length > 0) {
                setIsOpen(true);
              }
            }
          } catch (error) {
            console.error("Error fetching suggestions:", error);
            const jsonSuggestions = fetchCitySuggestions(newInputValue);
            setSuggestions(jsonSuggestions);

            // Ensure dropdown opens when there are suggestions
            if (jsonSuggestions.length > 0) {
              setIsOpen(true);
            }
          }
        } else {
          setSuggestions([]);
          setIsOpen(false);
        }
      }
    },
    [fetchCitySuggestions],
  );
  const cleanString = (str) => {
    if (!str) return "";
    return str.replace(/\s+/g, "-").replace(/,/g, "");
  };

  const buildPropertyTitleUrl = (property = {}) => {
    return [
      cleanString(property.loc_address),
      cleanString(property.loc_city),
      abbrState(property.state, "abbr") || property.state,
      property.zipcode,
    ]
      .filter(Boolean)
      .join("-");
  };
  const handleSelect = async (event, value) => {
    if (!value) return;

    event.preventDefault();
    event.stopPropagation();

    const searchValue = value.address;
    setInputValue(searchValue); // Keep the selected value in input

    if (value.source === "state") {
      const params = getUrlParams();
      params.delete("isCity");
      params.delete("isDirect");
      params.set("searchbar", searchValue);
      params.set("isState", "true");
      params.delete("initial");
      params.delete("reset");
      params.delete("search_attempted");

      const newUrl = `/search?${params.toString()}`;
      setSuggestions([]);
      setIsOpen(false);
      history.push(newUrl);
    } else if (value.source === "city" || !value.id) {
      const params = getUrlParams();
      params.delete("isState");
      params.delete("isDirect");
      params.set("searchbar", searchValue);
      params.set("isCity", "true");
      params.delete("search_attempted");

      const newUrl = `/search?${params.toString()}`;
      setSuggestions([]);
      setIsOpen(false);
      history.push(newUrl);
    } else if (value.id) {
      const titleUrl = buildPropertyTitleUrl(value);
      window.open(`/property/${value.id}/${titleUrl}`, "_blank");
      setSuggestions([]);
      setIsOpen(false);
    }
  };

  const handleSearchClick = () => {
    const triggerEmptySearchReset = () => {
      const params = new URLSearchParams(location.search);
      const defaultParams = global.defaultLocation
        ? new URLSearchParams(global.defaultLocation)
        : new URLSearchParams();

      // Keep active filters, but reset map viewport/search context to defaults.
      params.set("searchbar", "");
      params.set("page", "1");
      params.set("lat", defaultParams.get("lat") || global.defaultLat);
      params.set("long", defaultParams.get("long") || global.defaultLong);
      params.set("north", defaultParams.get("north") || global.defaultNorth);
      params.set("south", defaultParams.get("south") || global.defaultSouth);
      params.set("east", defaultParams.get("east") || global.defaultEast);
      params.set("west", defaultParams.get("west") || global.defaultWest);
      params.set(
        "zoom_level",
        defaultParams.get("zoom_level") || global.defaultZoomLevel,
      );
      params.set("title", defaultParams.get("title") || "United States");
      params.set("cities", defaultParams.get("cities") || "");
      params.set("state", defaultParams.get("state") || "");
      params.set("initial", "true");
      params.set("reset", "true");

      // Clear search-only flags.
      params.delete("inputReset");
      params.delete("search_attempted");
      params.delete("isDirect");
      params.delete("isState");
      params.delete("isCity");

      const newUrl = `${window.location.pathname}?${params.toString()}`;
      history.replace(newUrl);
    };

    if (inputValue) {
      // Check if the input exactly matches a state abbreviation or name
      const stateMatch = usStates.find(
        (state) =>
          state.abbr.toLowerCase() === inputValue.toLowerCase() ||
          state.name.toLowerCase() === inputValue.toLowerCase(),
      );

      const cityMatch = citiesData.find(
        (city) =>
          city.city.toLowerCase() === inputValue.toLowerCase() ||
          city.state_id.toLowerCase() === inputValue.toLowerCase(),
      );

      // Get appropriate URL params
      const params = getUrlParams();

      // Clear any previous flags
      params.delete("isState");
      params.delete("isCity");
      params.delete("isDirect");
      params.delete("initial");
      params.delete("reset");
      params.delete("search_attempted");
      params.delete("inputReset");
      params.set("page", "1"); // Reset to page 1 for new searches

      // Check if we're already on the search page
      const isOnSearchPage = location.pathname.includes("/search");

      // Don't check for duplicate searches when on search page - always allow the search
      if (stateMatch) {
        params.set("searchbar", stateMatch.name);
        params.set("isState", "true");
        const newUrl = `/search?${params.toString()}`;
        history.push(newUrl);
      } else if (cityMatch) {
        params.set("searchbar", cityMatch.city);
        params.set("isCity", "true");
        const newUrl = `/search?${params.toString()}`;
        history.push(newUrl);
      } else {
        // For direct searches, make sure we preserve the search query
        params.set("isDirect", "true");
        params.set("searchbar", inputValue);

        // If we're already on the search page, use replace instead of push to avoid adding to history
        const newUrl = `/search?${params.toString()}`;
        console.log("Initiating direct search with query:", inputValue);

        if (isOnSearchPage) {
          // Force a fresh search by adding a timestamp to prevent caching
          params.set("_t", Date.now());
          const freshUrl = `/search?${params.toString()}`;
          history.replace(freshUrl);
        } else {
          history.push(newUrl);
        }
      }

      // Clear old suggestions and close dropdown
      setSuggestions([]);
      setIsOpen(false);

      // Keep the input value - don't clear it automatically
    } else if (from === "home") {
      history.push(`/search?${global.defaultLocation}`);
    } else {
      // Empty search should always reset map bounds to defaults while preserving filters.
      triggerEmptySearchReset();
    }
  };

  // Add a keydown handler for the input to ensure it triggers suggestion fetching
  const handleKeyDown = (e) => {
    // Keep Enter behavior identical to search button click.
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchClick();
      return;
    }

    // Make sure dropdown opens on arrow down if there are suggestions
    if (e.key === "ArrowDown" && suggestions.length > 0) {
      setIsOpen(true);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const reset = urlParams.get("reset") || "";
    const inputReset = urlParams.get("inputReset") || "";
    const searchbarValue = urlParams.get("searchbar") || "";

    // Handle reset - clear input if explicit reset or inputReset
    if (reset === "true" || inputReset === "true") {
      setInputValue("");
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    // Sync input value with URL searchbar parameter
    if (searchbarValue !== inputValue) {
      setInputValue(searchbarValue);
    }
  }, [location.search]);

  // Force reset the suggestions state when clicking into the input
  const handleInputFocus = () => {
    if (inputValue && inputValue.trim() !== "") {
      // If there's already text, show suggestions immediately
      const jsonSuggestions = fetchCitySuggestions(inputValue);
      if (jsonSuggestions.length > 0) {
        setSuggestions(jsonSuggestions);
        setIsOpen(true);
      }
    }
  };

  const renderOption = (option) => {
    let address = option.address || "";

    address = address
      .replace(/\b\w+\sCounty\b/gi, "")
      .replace(/\s{2,}/g, " ")
      .trim();

    const query = inputValue || "";

    if (!query) {
      return (
        <div className="map-address-data">
          <span className="material-icons">
            {option.source === "state"
              ? "map"
              : option.source === "city"
                ? "location_city"
                : "location_on"}
          </span>
          {address}
        </div>
      );
    }

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedQuery})`, "gi");
    const parts = address.split(regex);

    return (
      <div
        className="map-address-data"
        style={{ display: "flex", alignItems: "center" }}
      >
        <span
          className="material-icons"
          style={{ marginRight: "8px", flexShrink: 0 }}
        >
          {option.source === "state"
            ? "map"
            : option.source === "city"
              ? "location_city"
              : "location_on"}
        </span>
        <div style={{ display: "inline" }}>
          {parts.map((part, index) => (
            <span
              key={index}
              style={{
                fontWeight:
                  part.toLowerCase() === query.toLowerCase() ? 700 : 400,
              }}
            >
              {part}
            </span>
          ))}
        </div>
      </div>
    );
  };
  const mobileWidth = useMediaQuery("(max-width:766px)");
  const filterOptions = (options, { inputValue }) => {
    const lowerInput = inputValue.toLowerCase();
    const stateMatch = usStates.find(
      (state) => state.abbr.toLowerCase() === lowerInput,
    );
    const filterText = stateMatch ? stateMatch.name.toLowerCase() : lowerInput;

    return options.filter((option) => {
      const optionAddress =
        typeof option === "string"
          ? option.toLowerCase()
          : (option?.address || "").toLowerCase();
      const optionSource = typeof option === "string" ? "" : option?.source;

      // API already returns ranked suggestions; don't over-filter on client.
      if (optionSource === "api") {
        return true;
      }

      return optionAddress.startsWith(filterText);
    });
  };
  return (
    <Autocomplete
      className={className}
      options={suggestions}
      getOptionLabel={(option) => {
        if (typeof option === "string") return option;
        if (!option) return "";
        return option.address || "";
      }}
      getOptionSelected={(option, value) =>
        (
          (typeof option === "string" ? option : option?.address) || ""
        ).toLowerCase() ===
          (
            (typeof value === "string" ? value : value?.address) || ""
          ).toLowerCase() && (option?.source || "") === (value?.source || "")
      }
      onChange={handleSelect}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      disableClearable
      open={isOpen && suggestions.length > 0}
      onOpen={() => {
        if (inputValue && suggestions.length > 0) {
          setIsOpen(true);
        }
      }}
      onClose={() => setIsOpen(false)}
      forcePopupIcon={true}
      openOnFocus={true}
      noOptionsText="No matching locations"
      renderOption={(option) => renderOption(option)}
      filterOptions={filterOptions}
      fullWidth={fullWidth}
      onKeyDown={handleKeyDown}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          size={size}
          variant={variant}
          name="searchName"
          placeholder={isHome ? placeholder : "Start your search"}
          fullWidth={fullWidth}
          onFocus={handleInputFocus}
          InputProps={{
            ...params.InputProps,
            startAdornment: isHome ? (
              <InputAdornment position="start">
                <span className="material-icons-outlined">location_on</span>
              </InputAdornment>
            ) : (
              ""
            ),

            endAdornment: (
              <InputAdornment position="end" onClick={handleSearchClick}>
                {isHome ? (
                  <span
                    className={mobileWidth ? "material-icons" : ""}
                    style={{
                      cursor: "pointer",
                      fontWeight: 500,
                    }}
                  >
                    {!mobileWidth ? "Search" : "search"}
                  </span>
                ) : (
                  <SearchIcon
                    onClick={handleSearchClick}
                    style={{ cursor: "pointer" }}
                  />
                )}
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
};

export default SearchBar;
